import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AgentWizardData } from '@/schemas/agent-wizard'
import { agentWizardDefaults, WIZARD_STEPS, getStepSchema } from '@/schemas/agent-wizard'
import type { AgentTemplate } from '@/schemas/agent-template'

interface AgentWizardState {
  // Current step (0-based, Step 0 is template selection)
  currentStep: number
  // Form data
  formData: Partial<AgentWizardData>
  // Selected template (full object for preview/display)
  selectedTemplate: AgentTemplate | null
  // Validation state per step
  stepValidation: Record<number, boolean>
  // Loading state
  isSubmitting: boolean
  // Error message
  error: string | null
  // Transient callback to save a pending new API key (not persisted to sessionStorage)
  _pendingApiKeySaver: (() => Promise<void>) | null

  // Actions
  setStep: (step: number) => void
  nextStep: () => boolean
  prevStep: () => void
  setFormData: (data: Partial<AgentWizardData>) => void
  updateField: <K extends keyof AgentWizardData>(field: K, value: AgentWizardData[K]) => void
  validateCurrentStep: () => boolean
  setStepValidation: (step: number, isValid: boolean) => void
  setSubmitting: (isSubmitting: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
  getCompleteData: () => AgentWizardData
  registerPendingApiKeySaver: (fn: (() => Promise<void>) | null) => void
  // Template-specific actions
  selectTemplate: (template: AgentTemplate | null) => void
  applyTemplate: (template: AgentTemplate) => void
  clearTemplate: () => void
  skipTemplateStep: () => void
}

const initialState = {
  currentStep: 0,
  formData: { ...agentWizardDefaults },
  selectedTemplate: null as AgentTemplate | null,
  stepValidation: {},
  isSubmitting: false,
  error: null,
  _pendingApiKeySaver: null as (() => Promise<void>) | null,
}

// Find the max step ID for bounds checking
const MAX_STEP = Math.max(...WIZARD_STEPS.map((s) => s.id))
const MIN_STEP = Math.min(...WIZARD_STEPS.map((s) => s.id))

export const useAgentWizardStore = create<AgentWizardState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStep: (step) => {
        if (step >= MIN_STEP && step <= MAX_STEP) {
          set({ currentStep: step })
        }
      },

      nextStep: () => {
        const { currentStep, validateCurrentStep } = get()
        const currentStepConfig = WIZARD_STEPS.find((s) => s.id === currentStep)

        // Step 0 (template) is always valid - it's optional
        if (currentStep === 0 || currentStepConfig?.optional) {
          if (currentStep < MAX_STEP) {
            set({ currentStep: currentStep + 1 })
            return true
          }
          return false
        }

        // Validate current step before proceeding
        if (!validateCurrentStep()) {
          return false
        }

        if (currentStep < MAX_STEP) {
          set({ currentStep: currentStep + 1 })
          return true
        }
        return false
      },

      prevStep: () => {
        const { currentStep } = get()
        if (currentStep > MIN_STEP) {
          set({ currentStep: currentStep - 1 })
        }
      },

      setFormData: (data) => {
        set((state) => ({
          formData: { ...state.formData, ...data },
        }))
      },

      updateField: (field, value) => {
        set((state) => ({
          formData: { ...state.formData, [field]: value },
        }))
      },

      validateCurrentStep: () => {
        const { currentStep, formData, setStepValidation } = get()
        const stepConfig = WIZARD_STEPS.find((s) => s.id === currentStep)
        const schema = getStepSchema(currentStep)

        // Optional steps are always valid
        if (stepConfig?.optional) {
          setStepValidation(currentStep, true)
          return true
        }

        if (!schema) {
          return true
        }

        // Extract only the fields for this step
        const stepFields = stepConfig?.fields ?? []
        const stepData: Record<string, unknown> = {}
        for (const field of stepFields) {
          stepData[field] = formData[field as keyof AgentWizardData]
        }

        const result = schema.safeParse(stepData)
        setStepValidation(currentStep, result.success)

        return result.success
      },

      setStepValidation: (step, isValid) => {
        set((state) => ({
          stepValidation: { ...state.stepValidation, [step]: isValid },
        }))
      },

      setSubmitting: (isSubmitting) => {
        set({ isSubmitting })
      },

      setError: (error) => {
        set({ error })
      },

      registerPendingApiKeySaver: (fn) => {
        set({ _pendingApiKeySaver: fn })
      },

      reset: () => {
        set({ ...initialState, _pendingApiKeySaver: null, selectedTemplate: null })
      },

      getCompleteData: () => {
        const { formData } = get()
        return {
          ...agentWizardDefaults,
          ...formData,
        } as AgentWizardData
      },

      // Template-specific actions
      selectTemplate: (template) => {
        set((state) => ({
          selectedTemplate: template,
          formData: {
            ...state.formData,
            selected_template_id: template?.id ?? null,
          },
        }))
      },

      applyTemplate: (template) => {
        // Apply template values to form data
        set((state) => ({
          selectedTemplate: template,
          formData: {
            ...state.formData,
            selected_template_id: template.id,
            // Pre-fill description from template (user can change)
            description: template.description,
            // Pre-fill icon from template
            icon: template.icon,
            // Pre-fill LLM config
            provider: template.llm_config?.provider ?? null,
            model: template.llm_config?.model ?? null,
            temperature: template.llm_config?.temperature ?? 0.7,
            max_tokens: template.llm_config?.max_tokens ?? 2048,
            // Pre-fill system prompt
            system_prompt: template.system_prompt,
            // Pre-fill memory config
            memory_type: template.memory_config?.type ?? null,
            memory_max_tokens: template.memory_config?.max_tokens ?? null,
            memory_window_size: template.memory_config?.window_size ?? null,
            // Pre-fill settings
            timeout: template.settings?.timeout ?? 60,
            max_retries: template.settings?.max_retries ?? 3,
            max_iterations: template.settings?.max_iterations ?? 10,
          },
        }))
      },

      clearTemplate: () => {
        set((state) => ({
          selectedTemplate: null,
          formData: {
            ...agentWizardDefaults,
            // Keep the name if already set
            name: state.formData.name ?? '',
          },
        }))
      },

      skipTemplateStep: () => {
        // Clear template selection and go to step 1
        set({
          selectedTemplate: null,
          currentStep: 1,
          formData: {
            ...agentWizardDefaults,
            selected_template_id: null,
          },
        })
      },
    }),
    {
      name: 'archon-agent-wizard',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        currentStep: state.currentStep,
        formData: state.formData,
        selectedTemplate: state.selectedTemplate,
        stepValidation: state.stepValidation,
      }),
    }
  )
)

// Selector hooks for specific parts of state
export const useWizardStep = () => useAgentWizardStore((state) => state.currentStep)
export const useWizardFormData = () => useAgentWizardStore((state) => state.formData)
export const useWizardSubmitting = () => useAgentWizardStore((state) => state.isSubmitting)
export const useWizardError = () => useAgentWizardStore((state) => state.error)
export const useWizardSelectedTemplate = () => useAgentWizardStore((state) => state.selectedTemplate)
